import { Injectable, inject } from '@angular/core';
import { Observable, Subscriber, of, throwError, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AccessRecipe,
  ContactsApi,
  FindContactByFiltersRequest,
  FindContactRequest,
  GroupsApi,
  PagedContactResponse,
  PagedGroups,
  RecipientSettings,
} from 'src/app/hickory-api';
import { AuthService } from '../../authentication/auth.service';
import { stringifySortedJson, getPageSlice, isInteger, toInteger } from '../../model/utility';
import { BaseDirectoryService } from '../base/base-directory.service';
import {
  DirectoryGroup,
  DirectoryUser,
  DirectoryType,
  PagedDirectoryUsers,
  PagedDirectoryGroups,
  PseudoDirectory,
} from '../base/models';
import { DIRECTORY_ID } from '../base/token';
import { AlertBuilderService } from 'src/app/alert/shared/alert-builder';
import { AlertRecipientsSearchingHelper } from 'src/app/alert/shared/alert-builder/helpers/alert-recipients-searching-helper';
import { AccessRecipeHelper } from '../../model/accessrecipe-helper';

@Injectable({ providedIn: 'root' }) // not a singleton -- see DirectoryInjector
export class HickoryDirectoryService extends BaseDirectoryService {
  private readonly contactsApi = inject(ContactsApi);
  private readonly groupsApi = inject(GroupsApi);
  private readonly authService = inject(AuthService);

  private readonly PAGE_SIZE = 500;
  classification: string;

  constructor() {
    const injectedId = inject(DIRECTORY_ID);

    super();
    // check permissions to determine if we should split the directory into Internal and Public
    this.splitDirectory = this.authService.hasPermission('hickory.split-hickory-directory-by-classification');
    this.directoryId = injectedId ?? PseudoDirectory.psuedoId();
    this.directoryType = DirectoryType.Hickory;
    // get the classification based on the pseudo directory id -- this will affect the output of this service
    this.classification = PseudoDirectory.getClassificationFromId(injectedId, this.splitDirectory);
  }

  private toIntegerIds(ids: Array<string | number>): Array<number> {
    return ids?.map((id) => toInteger(id))?.filter((id) => isInteger(id));
  }

  private toUserType(classification: string = this.classification): FindContactRequest.UserTypeEnum {
    const typedKey = classification as keyof typeof FindContactRequest.UserTypeEnum;
    return !!classification && Object.values(FindContactRequest.UserTypeEnum).includes(typedKey)
      ? FindContactRequest.UserTypeEnum[typedKey]
      : null;
  }

  putGroup(item: DirectoryGroup): void {
    // make sure that any child groups get this service's directoryId so that they show up in the right card
    if (item.root !== true && !!this.splitDirectory && !!this.classification && !!this.directoryId)
      item.directoryId = this.directoryId;
    super.putGroup(item);
  }

  clearClassification() {
    // don't use classification to filter requests
    this.classification = null;
  }

  getGroupChildren(id: string): Observable<DirectoryGroup[]> {
    if (!id) return of([]);
    return this.getSubGroups(id);
  }

  getGroup(id: string, refresh?: boolean): Observable<DirectoryGroup> {
    const intId = toInteger(id);
    if (!id || !intId || intId <= 0) return of(null);
    return new Observable<DirectoryGroup>((subscriber) => {
      const group = this.getCachedGroup(id + '');
      if (!!group && !refresh) {
        subscriber.next(group);
        subscriber.complete();
      } else {
        this.groupsApi.getGroupsByIdV21(intId).subscribe({
          next: (res) => {
            const dg = DirectoryGroup.fromHickoryFull(res.item, this.splitDirectory, group?.parentId);
            dg.externalId = dg.externalId ? dg.externalId : group?.externalId;
            dg.children = dg.children ?? group?.children;
            dg.root = group?.root ?? false;
            this.putGroup(dg);
            res.item.subgroups?.items?.forEach((child) => {
              if (!this.getCachedGroup(child.id))
                this.putGroup(DirectoryGroup.fromHickory(child, this.splitDirectory, dg.id));
            });
            if (!group && !dg.parentId && !dg.root) {
              // get the parent id if we don't have it already
              this.getParentGroupId(intId, dg, subscriber);
            } else {
              subscriber.next(dg);
              subscriber.complete();
            }
          },
          error: (err) => subscriber.error(err),
        });
      }
    });
  }

  private getParentGroupId(groupId: number, dg: DirectoryGroup, subscriber: Subscriber<DirectoryGroup>) {
    this.groupsApi.getGroupsV21([groupId], null, 1, 1).subscribe((resInner) => {
      const dgInner = DirectoryGroup.fromHickory(
        resInner?.items?.find((_) => true),
        this.splitDirectory,
      );
      dg.parentId = dgInner?.parentId;
      subscriber.next(dg);
      subscriber.complete();
    });
  }

  getGroupMembers(_id: string): Observable<DirectoryUser[]> {
    throw new Error('Method not implemented.');
  }

  getRootGroups() {
    this.incrGroupRequests();
    const pageSize = this.PAGE_SIZE;
    this.groupsApi.getGroupTreeNodes(null, null, 1, pageSize).subscribe({
      next: (res) => {
        res.items?.forEach((g) => {
          const dg = DirectoryGroup.fromHickoryNode(g, this.splitDirectory);
          dg.root = true;
          this.putGroup(dg);
        });
        this.onGroupsData();
      },
      error: (err) => {
        this.onGroupsError(err);
      },
    });
  }

  searchGroups(query: string): Observable<DirectoryGroup[]> {
    return new Observable<DirectoryGroup[]>((subscriber) => {
      this.groupsApi.getGroupsV21(null, null, 1, 10, query, this.classification as 'Internal' | 'Public').subscribe({
        next: (res) => {
          subscriber.next(res.items.map((g) => DirectoryGroup.fromHickory(g, this.splitDirectory)));
          subscriber.complete();
        },
        error: (err) => {
          subscriber.error(err);
        },
      });
    });
  }

  getGroupsById(ids: string[], page?: number, pageSize?: number): Observable<PagedDirectoryGroups> {
    page = page || 1;
    pageSize = pageSize || ids?.length || 0;
    const res: PagedDirectoryGroups = { items: [], page: page, pageSize: pageSize, totalItemCount: pageSize };
    if (!ids?.length) return of(res);

    // check to see if the groups are in the cache
    let needFetch = false;
    const cached = ids
      .map((id) => {
        const item = this.getCachedGroup(id);
        if (!item) needFetch = true;
        return item;
      })
      .filter((x) => !!x);
    if (!needFetch) {
      res.items = cached;
      return of(res);
    }

    const intIds = this.toIntegerIds(ids);
    return this.groupsApi.getGroupsV21(intIds, null, page, pageSize).pipe(map((r) => this.mapGroupResults(r)));
  }

  private mapGroupResults(res: PagedGroups): PagedDirectoryGroups {
    const mapped: PagedDirectoryGroups = { page: res.page, pageSize: res.pageSize, totalItemCount: res.totalItemCount };
    mapped.items = res?.items?.map((c) => {
      const m = DirectoryGroup.fromHickory(c, this.splitDirectory);
      this.putGroup(m);
      return m;
    });
    return mapped;
  }

  private getSubGroups(groupId): Observable<DirectoryGroup[]> {
    if (!groupId) return of([]);
    this.incrGroupRequests();
    const pageSize = this.PAGE_SIZE;
    const intId = toInteger(groupId);

    return this.groupsApi.getGroupTreeNodes(intId, null, 1, pageSize).pipe(
      map((res) => {
        const data = res.items?.map((g) => {
          const dg = DirectoryGroup.fromHickoryNode(g, this.splitDirectory, groupId);
          this.putGroup(dg);
          return dg;
        });
        this.onGroupsData();
        return data;
      }),
      catchError((err) => {
        this.onGroupsError(err);
        return throwError(() => err);
      }),
    );
  }

  private onGroupsData() {
    this.groupData$.next(this.cachedGroups);
    this.decrGroupRequests();
  }

  private onGroupsError(err: unknown) {
    this.groupData$.error(err);
    this.decrGroupRequests();
  }

  getUsers(query?: string, page?: number, pageSize?: number): Observable<PagedDirectoryUsers> {
    if (!query?.length) query = undefined;
    const req: FindContactRequest = {
      includeNestedGroups: false,
      chatEnabledOnly: false,
      includeAsset: false,
      userType: this.toUserType(),
      search: query,
      page: page ?? 1,
      pageSize: pageSize ?? 25,
    };
    return this.contactsApi.findContacts(req).pipe(
      map((res) => {
        const mapped: PagedDirectoryUsers = {
          page: res.page,
          pageSize: res.pageSize,
          totalItemCount: res.totalItemCount,
        };
        mapped.items = res?.items?.map((c) => {
          const u = DirectoryUser.fromHickory(c, this.splitDirectory);
          this.putUser(u);
          return u;
        });
        return mapped;
      }),
    );
  }

  getUsersById(
    ids: string[],
    groupIds?: string[],
    page?: number,
    pageSize?: number,
    sort?: string,
  ): Observable<PagedDirectoryUsers> {
    page = page || 1;
    pageSize = pageSize || (ids?.length ?? 0);
    const res: PagedDirectoryUsers = { items: [], page: page, pageSize: pageSize, totalItemCount: pageSize };
    if (!ids?.length && !groupIds?.length) return of(res);

    // check to see if the users are in the cache
    if (!groupIds?.length) {
      let needFetch = false;
      const cached = ids
        .map((id) => {
          const item = this.getCachedUser(id);
          if (!item) needFetch = true;
          return item;
        })
        .filter((x) => !!x);
      if (!needFetch) {
        res.items = cached;
        return of(res);
      }
    }

    // handle the case where we're resolving a large number of user IDs in batches
    if (ids?.length > pageSize) {
      ids = getPageSlice(ids, page, pageSize);
      page = 1;
    }

    const intIds = this.toIntegerIds(ids);
    const groupIntIds = this.toIntegerIds(groupIds);
    const req: FindContactRequest = {
      includeNestedGroups: !!groupIds?.length,
      chatEnabledOnly: false,
      includeAsset: false,
      userType: this.toUserType(),
      contactIds: intIds,
      groupIds: groupIntIds,
      page: page,
      pageSize: pageSize,
      sort: sort,
    };
    return this.contactsApi.findContacts(req).pipe(
      map((res) => {
        return this.mapContactResults(res);
      }),
    );
  }

  private mapContactResults(res: PagedContactResponse): PagedDirectoryUsers {
    const mapped: PagedDirectoryUsers = { page: res.page, pageSize: res.pageSize, totalItemCount: res.totalItemCount };
    mapped.items = res?.items?.map((c) => {
      const m = DirectoryUser.fromHickory(c, this.splitDirectory);
      this.putUser(m);
      return m;
    });
    return mapped;
  }

  private filterUserIdsByType(ids: Array<number>): Array<number> { return this.filterIdsByType(ids, false); }
  private filterGroupIdsByType(ids: Array<number>): Array<number> { return this.filterIdsByType(ids, true); }
  private filterIdsByType(ids: Array<number>, isGroup: boolean): Array<number> {
    if (!ids || !this.classification) return ids;
    const filteredIds = new Array<number>();
    ids.forEach((id) => {
      const item = isGroup ? this.getCachedGroup(id) : this.getCachedUser(id);
      // If we don't have the item in cache, include the ID by default. Otherwise, include the
      // ID only if the item classification matches the directory classification
      if (!item || item?.classification === this.classification) filteredIds.push(id);
    });

    return filteredIds;
  }

  totalRecipientCount(alertBuilder: AlertBuilderService): Observable<number> {
    const settings = alertBuilder.recipientSettings();
    const contactIds = this.prepareContactIds(settings);
    const groupIds = this.prepareGroupIds(settings);
    const filters = this.prepareSearchFilters(alertBuilder);

    if (!groupIds.length && !contactIds.length && !filters.length) return of(0);

    const contactCount$ = this.getContactsCount(contactIds, groupIds);
    const filterCount$ = this.getUserCountBySearch(filters);

    return forkJoin([contactCount$, filterCount$]).pipe(
      map(([contactCount, filterCount]) => contactCount + filterCount),
    );
  }

  private prepareContactIds(settings: RecipientSettings): number[] {
    const contactIds = settings.contactIds ?? [];
    return this.filterUserIdsByType(contactIds);
  }

  private prepareGroupIds(settings: RecipientSettings): number[] {
    let groupIds = settings.groupIds ?? [];
    groupIds = groupIds.concat(settings.mandatoryGroupIds ?? []);
    return this.filterGroupIdsByType(groupIds);
  }

  private prepareSearchFilters(alertBuilder: AlertBuilderService): AccessRecipe[] {
    const filters = alertBuilder.recipientSettings()?.searchFilters ?? [];
    return filters
      .filter((f) => AccessRecipeHelper.isValid(f) && AlertRecipientsSearchingHelper.isValidForDirectory(f, this.directoryId, this.splitDirectory))
      .map((f) => AlertRecipientsSearchingHelper.resolveFilter(f, alertBuilder))
      .flatMap((f) => AlertRecipientsSearchingHelper.withClassificationFilter(f, this.directoryId, this.splitDirectory));
  }

  /**
   * Gets count of contacts based on direct selection (contactIds/groupIds)
   */
  private getContactsCount(contactIds: number[], groupIds: number[]): Observable<number> {
    if (!groupIds.length && !contactIds.length) {
      return of(0);
    }

    const req: FindContactRequest = {
      contactIds: contactIds,
      groupIds: groupIds,
      includeNestedGroups: true,
      chatEnabledOnly: false,
      includeAsset: false,
      //userType: this.toUserType(), // ROAD-7734 -- don't include this on the request
      page: 1,
      pageSize: 1,
    };

    const requestKey = stringifySortedJson(req);
    return this.countRequestCache.executeRequest(
      requestKey,
      () => this.contactsApi.findContacts(req),
      (res) => res.totalItemCount,
      {
        ttlMs: 5 * 60 * 1000,
      },
    );
  }

  /**
   * Gets count of contacts based on search filters
   */
  getUserCountBySearch(filters: AccessRecipe[]): Observable<number> {
    if (!filters?.length || !filters?.every((f) => AccessRecipeHelper.isValid(f))) {
      return of(0);
    }

    const req: FindContactByFiltersRequest = {
      filters: filters.map((f) => f.filter),
      page: 1,
      pageSize: 1,
    };

    const requestKey = stringifySortedJson(req);
    return this.countRequestCache.executeRequest(
      requestKey,
      () => this.contactsApi.findContactsByFilters(req),
      (res) => res.totalItemCount,
      {
        ttlMs: 5 * 60 * 1000,
      },
    );
  }

  /**
   * Gets users based on search filters
   */
  getUsersBySearch(
    filters: AccessRecipe[],
    page?: number,
    pageSize?: number,
    _?: string,
  ): Observable<PagedDirectoryUsers> {
    if (!filters?.length || !filters?.every((f) => AccessRecipeHelper.isValid(f))) return of(null);

    const req: FindContactByFiltersRequest = {
      filters: filters.map((f) => f.filter),
      page: page ?? 1,
      pageSize: pageSize ?? 25,
    };

    return this.contactsApi.findContactsByFilters(req).pipe(
      map((res) => {
        const mapped: PagedDirectoryUsers = {
          page: res.page,
          pageSize: res.pageSize,
          totalItemCount: res.totalItemCount,
        };
        mapped.items = res?.items?.map((c) => {
          return DirectoryUser.fromHickory(c, this.splitDirectory);
        });
        return mapped;
      }),
    );
  }
}
